import React from "react";

// InboxArchiveSection — the per-tab archive footer row (design 1c/1d + 5i).
//
// Product decision (Marin, overrides the design which omits the row entirely at
// zero): the row is ALWAYS rendered, on desktop and mobile (one code path —
// nothing hides it under md). At zero it shows only the archive icon +
// "Archive" + chevron and, when expanded, a static read-only note.
//
// D24 gives the archive TWO species: chats the viewer archived MANUALLY (still
// writable, return on a new message) and chats that EXPIRED (timer, read-only,
// permanent). The footer count reads "N archived by you · M expired" (frame 5a);
// the expanded body renders the caller's grouped lists (5i). Children mount only
// when expanded AND there is at least one archived chat, so the archived-channel
// queries never fire while the tab has none.
//
// Props:
//   manualCount   number  — chats the viewer archived (writable).
//   expiredCount  number  — timer-expired chats (read-only).
//   expanded      bool    — whether the section is open.
//   onToggle      () => void — toggles the section.
//   children      node    — the grouped archived-rows lists (5i), shown only when
//                           expanded && (manualCount + expiredCount) > 0.
const InboxArchiveSection = ({
	manualCount = 0,
	expiredCount = 0,
	expanded,
	onToggle,
	children,
}) => {
	const total = manualCount + expiredCount;
	// "3 archived by you · 6 expired" — each half drops when it is zero, so a
	// tab with only expired chats reads "· 6 expired" cleanly (no empty "0").
	const countParts = [];
	if (manualCount > 0) countParts.push(`${manualCount} archived by you`);
	if (expiredCount > 0) countParts.push(`${expiredCount} expired`);
	const countLabel = countParts.join(" · ");
	return (
		<div className="mt-2">
			<button
				type="button"
				onClick={onToggle}
				className="flex w-full items-center gap-[9px] px-4 py-3 border-t border-[#E4E9F0] bg-[#FAFBFC] hover:bg-[#F5F7FA] transition-colors"
			>
				<svg
					width="14"
					height="14"
					viewBox="0 0 16 16"
					fill="none"
					className="flex-shrink-0"
				>
					<rect
						x="2"
						y="3"
						width="12"
						height="3.5"
						rx="1"
						stroke="#98A2B3"
						strokeWidth="1.5"
					/>
					<rect
						x="3.2"
						y="6.5"
						width="9.6"
						height="6.5"
						rx="1"
						stroke="#98A2B3"
						strokeWidth="1.5"
					/>
					<path
						d="M6.5 9.5h3"
						stroke="#98A2B3"
						strokeWidth="1.5"
						strokeLinecap="round"
					/>
				</svg>
				<span className="text-[12.5px] font-semibold text-[#667085]">
					Archive
				</span>
				{countLabel && (
					<span className="text-[11.5px] text-[#98A2B3]">{countLabel}</span>
				)}
				<svg
					className="ml-auto flex-shrink-0"
					width="12"
					height="12"
					viewBox="0 0 12 12"
					fill="none"
					style={{
						transform: expanded ? "none" : "rotate(-90deg)",
						transition: "transform .15s",
					}}
				>
					<path
						d="M2.5 4L6 8L9.5 4"
						stroke="#98A2B3"
						strokeWidth="1.6"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</button>
			{expanded && (
				// The section is a PINNED column footer (design 1c/5a bottom strip,
				// Marin 2026-07-16) — the expanded panel scrolls inside itself so it
				// never swallows the conversation list above.
				<div className="max-h-[45vh] overflow-y-auto">
					{total > 0 ? (
						children
					) : (
						// Zero-state (Marin's product call): a static read-only note; no
						// ChannelList is mounted, so the archived query never fires at zero.
						<div className="bg-[#FAFBFC] px-4 py-3">
							<p className="text-[11.5px] font-normal text-[#98A2B3]">
								Nothing archived yet — chats you archive stay writable and return
								on a new message; expired chats land here read-only.
							</p>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

// A small mono-caps group header for the 5i archive species groups. Rendered by
// the caller above each species' ChannelList, only when that group has rows.
export const ArchiveGroupHeader = ({ children }) => (
	<div
		className="px-4 pt-3 pb-1 bg-[#FAFBFC]"
		style={{
			font: "600 9.5px ui-monospace,Menlo,monospace",
			color: "#98A2B3",
			letterSpacing: ".06em",
		}}
	>
		{children}
	</div>
);

export default InboxArchiveSection;
